import React, { useMemo } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { TeamScheduleAdminScreenQuery } from "./__generated__/TeamScheduleAdminScreenQuery.graphql";

import { TeamScheduleAdminScreenCreateMutation } from "./__generated__/TeamScheduleAdminScreenCreateMutation.graphql";
import { useAlert } from "../../common/Alert";
import { TeamScheduleAdminList } from "./TeamScheduleAdminList";
import { useNavigationHeader } from "../../common/navigationHeaderOptions";
import { TeamScheduleAdminNavigationProp } from "./TeamScheduleAdminStack";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { CurrentTeamIdProvider } from "widgets/src/CurrentTeamIdContext";
import { useHasFeatureFlag } from "@/src/useHasFeatureFlag";
import { FeatureFlags } from "@/src/config";

export function InnerTeamScheduleAdminScreenComponent(props: {
  teamId: string;
  fetchKey: string;
  navigation: TeamScheduleAdminNavigationProp<"TeamScheduleAdminMain">;
}) {
  const data = useLazyLoadQuery<TeamScheduleAdminScreenQuery>(
    graphql`
      query TeamScheduleAdminScreenQuery($teamId: ID!) {
        store {
          team(id: $teamId) {
            id
            name
            roles {
              id
              name
            }
            routineTemplates {
              id
            }
            routines(type: STANDARD, includeOrg: true) {
              id
              ...TeamScheduleAdminList_routines
            }
          }
        }
      }
    `,
    {
      teamId: props.teamId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const hasEmployeeRoleFlag = useHasFeatureFlag(FeatureFlags.EmployeeRole);
  const intl_tokens = useIntlTokens();

  useNavigationHeader({
    type: "CLOSE",
    title: data.store.team?.name ?? "",
    onClose: () => {
      props.navigation.goBack();
    },
    disableBorder: true,
    more: [
      {
        label: intl_tokens.ShowArchived,
        icon: "archive",
        type: "navigation",
        onPress: () => {
          props.navigation.navigate("TeamScheduleAdminArchived", {
            teamId: props.teamId,
          });
        },
      },
    ],
  });

  const [createMutation, createRoutineInFlight] =
    useMutation<TeamScheduleAdminScreenCreateMutation>(graphql`
      mutation TeamScheduleAdminScreenCreateMutation($teamId: ID!) {
        createRoutine(input: { title: "", teamId: $teamId, type: EVENT_ITEM }) {
          id
          ...RoutineEditMainRoutine_Routine
        }
      }
    `);

  const Alert = useAlert();

  const hasTemplates = useMemo(
    () => (data.store.team?.routineTemplates?.length ?? 0) > 0,
    [data.store.team?.routineTemplates]
  );

  return (
    <CurrentTeamIdProvider value={props.teamId}>
      <TeamScheduleAdminList
        routines={data.store.team?.routines ?? []}
        roles={data.store.team?.roles ?? []}
        hasEmployeeRoleFlag={hasEmployeeRoleFlag}
        onOpenTemplates={
          hasTemplates
            ? ({ freq, selectedDaysFilter }) => {
                props.navigation.navigateDeprecated(
                  "TeamScheduleAdminTemplates",
                  {
                    teamId: props.teamId,
                    freq,
                    selectedDaysFilter,
                  }
                );
              }
            : undefined
        }
        onEditRoutine={(params) => {
          props.navigation.navigateDeprecated("RoutineEdit", {
            ...params,
          });
        }}
        onCreateRoutine={() => {
          if (createRoutineInFlight) {
            return null;
          }
          return new Promise<string>((resolve, reject) => {
            createMutation({
              variables: {
                teamId: props.teamId,
              },
              onError: (err) => {
                Alert.error("Error", err.message);
                reject(err);
              },
              onCompleted: (res) => {
                if (res.createRoutine?.id) {
                  resolve(res.createRoutine.id);
                }
              },
            });
          });
        }}
      />
    </CurrentTeamIdProvider>
  );
}
